import { ReactNode } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Home } from 'lucide-react';
import Sidebar from './Sidebar';
import UserMenu from '../auth/UserMenu';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";

interface PageLayoutProps {
  children: ReactNode;
  title: string;
  description?: string;
  showBackToDashboard?: boolean;
  badge?: ReactNode;
  // When true, do not apply overflow-hidden on the content wrapper (useful for sticky sub-layouts)
  disableOverflowHidden?: boolean;
}

const PageLayout = ({ 
  children, 
  title, 
  description, 
  showBackToDashboard = false,
  badge,
  disableOverflowHidden = false
}: PageLayoutProps) => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(segment => segment);

  // Add breadcrumbs only if not on the dashboard
  const showBreadcrumbs = location.pathname !== '/dashboard';

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Content wrapper that respects sidebar width on desktop */}
        <div className="md:ml-64 flex-1 transition-all duration-300 min-w-0">
          <div className="w-full max-w-full mx-auto px-3 sm:px-4 lg:px-6 py-4 pt-16 md:pt-6">
            {/* Header with breadcrumbs and user menu */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex-1 min-w-0">
                {showBreadcrumbs && (
                  <Breadcrumb className="mb-2">
                    <BreadcrumbList className="flex-wrap">
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/dashboard" className="flex items-center">
                            <Home size={14} />
                          </Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      
                      {pathSegments.map((segment, index) => {
                        const path = `/${pathSegments.slice(0, index + 1).join('/')}`;
                        const isLast = index === pathSegments.length - 1;
                        
                        return (
                          <BreadcrumbItem key={path}>
                            <BreadcrumbSeparator />
                            {isLast ? (
                              <BreadcrumbPage className="truncate">{title}</BreadcrumbPage>
                            ) : (
                              <BreadcrumbLink asChild>
                                <Link to={path} className="truncate">
                                  {segment.replace(/-/g, ' ')}
                                </Link>
                              </BreadcrumbLink>
                            )}
                          </BreadcrumbItem>
                        );
                      })}
                    </BreadcrumbList>
                  </Breadcrumb>
                )}
              </div>
              
              <div className="flex-shrink-0">
                <UserMenu />
              </div>
            </div>
            
            {/* Page title and description */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold break-words">{title}</h1>
                {badge}
              </div>
              {description && <p className="text-muted-foreground mt-2 break-words text-sm sm:text-base">{description}</p>}
            </div>
            
            {/* Main content */}
            <div className={`w-full ${disableOverflowHidden ? '' : 'overflow-hidden'} min-w-0`}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageLayout;
